import { useCallback, useEffect, useRef } from 'react'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useTranslation } from 'react-i18next'
import { useAnalyticsApi, useReportsApi } from '@/hooks'
import { processBenchmarkingFilters } from '../utils/processBenchmarkingFilters'

export const useExportBenchmarkingReport = (reportId: string) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')

    const { useGetReport } = useReportsApi()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const { useBenchmarkingQuery } = useAnalyticsApi()

    const triggered = useRef(false)

    const filters = report
        ? processBenchmarkingFilters(report.filters)
        : undefined

    const {
        data,
        refetch: refetchBenchmarking,
        downloadXlsx,
    } = useBenchmarkingQuery(filters, {
        enabled: !!report && triggered.current,
    })

    const downloadData = useCallback(() => {
        downloadXlsx().catch(() => {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        })
    }, [addAlert, downloadXlsx, t])

    useEffect(() => {
        if (triggered.current) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, data])

    const exportBenchmarkingReport = () => {
        triggered.current = true

        if (!report) {
            void refetchReport()
            return
        }

        if (!data) {
            void refetchBenchmarking()
            return
        }

        triggered.current = false
        downloadData()
    }

    return {
        exportBenchmarkingReport,
    }
}
